import React, { useMemo } from 'react';
import { StaffMember, AttendanceRecord } from '../types';
import { Icons } from './ui/Icons';

interface AttendancePrintPageProps {
    periodStart: string;
    periodEnd: string;
    records: AttendanceRecord[];
    staffList: StaffMember[];
    onDone: () => void;
}

const AttendancePrintPage: React.FC<AttendancePrintPageProps> = ({
    periodStart,
    periodEnd,
    records,
    staffList,
    onDone
}) => {
    const periodRecords = useMemo(() => {
        return records.filter(r => r.date >= periodStart && r.date <= periodEnd && (r.status === 'APPROVED' || r.status === 'AUTO_APPROVED'));
    }, [records, periodStart, periodEnd]);

    const employeeTotals = useMemo(() => {
        const totals: { [staffId: string]: { name: string, totalHours: number, extraHours: number } } = {};
        
        periodRecords.forEach(record => {
            if (!totals[record.employeeId]) {
                const staff = staffList.find(s => s.id === record.employeeId);
                totals[record.employeeId] = {
                    name: staff?.name || 'Unknown Staff',
                    totalHours: 0,
                    extraHours: 0
                };
            }
            totals[record.employeeId].totalHours += record.totalHours;
            totals[record.employeeId].extraHours += record.extraHours;
        });

        return Object.values(totals).sort((a, b) => a.name.localeCompare(b.name));
    }, [periodRecords, staffList]);

    const overallTotalHours = employeeTotals.reduce((sum, t) => sum + t.totalHours, 0);
    const overallExtraHours = employeeTotals.reduce((sum, t) => sum + t.extraHours, 0);

    const handlePrint = () => {
        window.print();
    };

    return (
        <div className="min-h-screen bg-white p-8 print:p-0">
            <div className="max-w-4xl mx-auto">
                <div className="flex justify-between items-center mb-8 print:hidden">
                    <button 
                        onClick={onDone}
                        className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <Icons.ArrowLeft className="w-4 h-4" />
                        Back to Portal
                    </button>
                    <button 
                        onClick={handlePrint}
                        className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 transition-all shadow-sm"
                    >
                        <Icons.Printer className="w-4 h-4" />
                        Print Report
                    </button>
                </div>

                <div className="border-b-2 border-gray-900 pb-6 mb-8 text-center">
                    <h1 className="text-3xl font-black uppercase tracking-tighter text-gray-900 mb-2">Biweekly Attendance Report</h1>
                    <p className="text-lg font-bold text-gray-600">Period: {periodStart} to {periodEnd}</p>
                </div>

                <div className="mb-12">
                    <h2 className="text-xl font-bold uppercase tracking-wider text-gray-900 mb-4 border-b border-gray-200 pb-2">Employee Summary</h2>
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b-2 border-gray-900">
                                <th className="py-3 font-black uppercase text-sm">Employee Name</th>
                                <th className="py-3 font-black uppercase text-sm text-right">Total Hours</th>
                                <th className="py-3 font-black uppercase text-sm text-right">Extra/Less Hours</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {employeeTotals.length === 0 ? (
                                <tr>
                                    <td colSpan={3} className="py-8 text-center text-gray-500 italic">No approved attendance records for this period.</td>
                                </tr>
                            ) : (
                                employeeTotals.map((total, idx) => (
                                    <tr key={idx}>
                                        <td className="py-3 font-bold text-gray-800">{total.name}</td>
                                        <td className="py-3 text-right font-mono font-bold">{(total.totalHours ?? 0).toFixed(2)}h</td>
                                        <td className="py-3 text-right font-mono">
                                            <span className={(total.extraHours ?? 0) > 0 ? 'text-emerald-700' : (total.extraHours ?? 0) < 0 ? 'text-rose-700' : 'text-gray-400'}>
                                                {(total.extraHours ?? 0) > 0 ? `+${(total.extraHours ?? 0).toFixed(2)}` : (total.extraHours ?? 0).toFixed(2)}h
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                        <tfoot>
                            <tr className="border-t-2 border-gray-900 bg-gray-50">
                                <td className="py-4 text-right font-black uppercase text-gray-900">Grand Total</td>
                                <td className="py-4 text-right font-black text-lg text-indigo-900">{(overallTotalHours ?? 0).toFixed(2)}h</td>
                                <td className="py-4 text-right font-black text-lg">
                                    <span className={(overallExtraHours ?? 0) > 0 ? 'text-emerald-700' : (overallExtraHours ?? 0) < 0 ? 'text-rose-700' : 'text-gray-400'}>
                                        {(overallExtraHours ?? 0) > 0 ? `+${(overallExtraHours ?? 0).toFixed(2)}` : (overallExtraHours ?? 0).toFixed(2)}h
                                    </span>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <div className="mt-12 pt-8 border-t border-gray-200 text-center text-xs text-gray-400 uppercase tracking-widest">
                    Generated on {new Date().toLocaleString()} • Staff Management System
                </div>
            </div>
        </div>
    );
};

export default AttendancePrintPage;
